'use client';

import { useCallback, useEffect, useState } from 'react';
import {
  CreditCard,
  Download,
  ImageIcon,
  Loader2,
  Sparkles,
  User,
} from 'lucide-react';
import { useTranslations } from 'next-intl';
import { toast } from 'sonner';

import { AIMediaType, AITaskStatus } from '@/extensions/ai/types';
import {
  ImageUploader,
  ImageUploaderValue,
  LazyImage,
} from '@/shared/blocks/common';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Progress } from '@/shared/components/ui/progress';
import { ScrollArea } from '@/shared/components/ui/scroll-area';
import { useAppContext } from '@/shared/contexts/app';
import {
  trackPhotoEffects,
  trackImageUpload,
  trackGenerateClick,
} from '@/shared/lib/analytics';

interface OldPhotoRestorationProps {
  maxSizeMB?: number;
  srOnlyTitle?: string;
}

interface GeneratedImage {
  id: string;
  url: string;
}

interface BackendTask {
  id: string;
  status: string;
  provider: string;
  model: string;
  prompt: string | null;
  taskInfo: string | null;
  taskResult: string | null;
}

const POLL_INTERVAL = 5000;
const GENERATION_TIMEOUT = 180000;
const MODEL = 'bytedance/seedream-4';
const PROVIDER = 'replicate';
const COST_CREDITS = 1;

// Built-in prompt for old photo restoration
const RESTORATION_PROMPT = 'Restore this old photo by converting it to full color with natural, realistic tones. Enhance clarity, remove scratches, stains, and damage. Improve sharpness and detail while preserving the original composition, faces, and historical authenticity. Make it look professionally restored with vibrant, lifelike colors.';

function parseTaskResult(taskResult: string | null): any {
  if (!taskResult) {
    return null;
  }

  try {
    return JSON.parse(taskResult);
  } catch (error) {
    console.warn('Failed to parse taskResult:', error);
    return null;
  }
}

function extractImageUrls(result: any): string[] {
  if (!result) {
    return [];
  }

  const output = result.output ?? result.images ?? result.data;

  if (!output) {
    return [];
  }

  if (typeof output === 'string') {
    return [output];
  }

  if (Array.isArray(output)) {
    return output
      .flatMap((item) => {
        if (!item) return [];
        if (typeof item === 'string') return [item];
        if (typeof item === 'object') {
          const candidate =
            item.url ?? item.uri ?? item.image ?? item.src ?? item.imageUrl;
          return typeof candidate === 'string' ? [candidate] : [];
        }
        return [];
      })
      .filter(Boolean);
  }

  if (typeof output === 'object') {
    const candidate =
      output.url ?? output.uri ?? output.image ?? output.src ?? output.imageUrl;
    if (typeof candidate === 'string') {
      return [candidate];
    }
  }

  return [];
}

export function OldPhotoRestoration({
  maxSizeMB = 24,
  srOnlyTitle,
}: OldPhotoRestorationProps) {
  const t = useTranslations('photo-effects.old-photo-restoration');

  const [referenceImageItems, setReferenceImageItems] = useState<
    ImageUploaderValue[]
  >([]);
  const [referenceImageUrls, setReferenceImageUrls] = useState<string[]>([]);
  const [generatedImages, setGeneratedImages] = useState<GeneratedImage[]>([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [generationStartTime, setGenerationStartTime] = useState<number | null>(
    null
  );
  const [taskStatus, setTaskStatus] = useState<AITaskStatus | null>(null);
  const [downloadingImageId, setDownloadingImageId] = useState<string | null>(null);

  const { user, isCheckSign, setIsShowSignModal, fetchUserCredits } =
    useAppContext();

  const remainingCredits = user?.credits?.remainingCredits ?? 0;

  const handleGenerate = async () => {
    if (referenceImageUrls.length === 0) {
      toast.error(t('errors.no-image'));
      return;
    }

    if (isCheckSign) {
      setIsShowSignModal(true);
      return;
    }

    if (remainingCredits < COST_CREDITS) {
      toast.error(t('errors.insufficient-credits'));
      return;
    }

    // Track generate button click
    trackGenerateClick({
      effect_id: 'old-photo-restoration',
      effect_name: 'Old Photo Restoration',
      is_custom: false,
      has_custom_prompt: false,
      credits_cost: COST_CREDITS,
      remaining_credits: remainingCredits,
    });

    setIsGenerating(true);
    setProgress(0);
    setTaskStatus(null);
    setGenerationStartTime(Date.now());

    try {
      const options: any = {
        image_input: referenceImageUrls,
      };

      const response = await fetch('/api/ai/generate', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          mediaType: AIMediaType.IMAGE,
          scene: 'image-to-image',
          provider: PROVIDER,
          model: MODEL,
          prompt: RESTORATION_PROMPT,
          options,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Generation failed');
      }

      const { code, message, data } = await response.json();
      if (code !== 0) {
        throw new Error(message || 'Generation failed');
      }

      const newTaskId = data?.id;
      if (!newTaskId) {
        throw new Error('Task id missing in response');
      }

      // Check if task completed immediately
      if (data.status === AITaskStatus.SUCCESS && data.taskInfo) {
        const parsedResult = parseTaskResult(data.taskInfo);
        const imageUrls = extractImageUrls(parsedResult);

        if (imageUrls.length > 0) {
          // Only take the first image for old photo restoration
          const firstImageUrl = imageUrls[0];
          const timestamp = Date.now();
          const newImageId = `${newTaskId}-${timestamp}`;

          const newImage: GeneratedImage = {
            id: newImageId,
            url: firstImageUrl,
          };

          // Replace the entire array instead of appending
          setGeneratedImages([newImage]);
          toast.success(t('success.restored'));

          trackPhotoEffects({
            effect: 'old-photo-restoration',
            credits_used: COST_CREDITS,
          });

          setIsGenerating(false);
          setProgress(100);
          setGenerationStartTime(null);
          setTaskStatus(null);
          await fetchUserCredits();
          return;
        }
      }

      setTaskId(newTaskId);
      await fetchUserCredits();
    } catch (error: any) {
      console.error('Image generation error:', error);
      toast.error(error.message || t('errors.generation-failed'));
      setIsGenerating(false);
      setProgress(0);
      setTaskStatus(null);
      setGenerationStartTime(null);
    }
  };

  useEffect(() => {
    if (!taskId || !isGenerating) {
      return;
    }

    let cancelled = false;

    const pollTask = async (): Promise<boolean> => {
      try {
        if (
          generationStartTime &&
          Date.now() - generationStartTime > GENERATION_TIMEOUT
        ) {
          toast.error(t('errors.generation-timeout'));
          setIsGenerating(false);
          setProgress(0);
          setTaskId(null);
          setGenerationStartTime(null);
          setTaskStatus(null);
          return true;
        }

        const response = await fetch('/api/ai/query', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ taskId }),
        });

        if (!response.ok) {
          throw new Error('Failed to fetch task status');
        }

        const { code, message, data } = await response.json();
        if (code !== 0) {
          throw new Error(message || 'Query task failed');
        }

        const task = data as BackendTask;
        const currentStatus = task.status as AITaskStatus;
        setTaskStatus(currentStatus);

        const parsedResult = parseTaskResult(task.taskInfo);
        const imageUrls = extractImageUrls(parsedResult);

        if (currentStatus === AITaskStatus.PENDING) {
          setProgress((prev) => Math.max(prev, 20));
          return false;
        }

        if (currentStatus === AITaskStatus.PROCESSING) {
          if (imageUrls.length > 0) {
            setProgress((prev) => Math.max(prev, 85));
          } else {
            setProgress((prev) => Math.min(prev + 10, 80));
          }
          return false;
        }

        if (currentStatus === AITaskStatus.SUCCESS) {
          if (imageUrls.length === 0) {
            toast.error('No image generated');
          } else {
            // Only take the first image for old photo restoration
            const firstImageUrl = imageUrls[0];
            const timestamp = Date.now();
            const newImageId = `${taskId}-${timestamp}`;

            const newImage: GeneratedImage = {
              id: newImageId,
              url: firstImageUrl,
            };

            // Replace the entire array instead of appending
            setGeneratedImages([newImage]);
            toast.success(t('success.restored'));

            trackPhotoEffects({
              effect: 'old-photo-restoration',
              credits_used: COST_CREDITS,
            });
          }

          setProgress(100);
          setIsGenerating(false);
          setTaskId(null);
          setGenerationStartTime(null);
          setTaskStatus(null);
          return true;
        }

        if (currentStatus === AITaskStatus.FAILED) {
          const errorMessage = parsedResult?.errorMessage || t('errors.generation-failed');
          toast.error(errorMessage);
          setIsGenerating(false);
          setProgress(0);
          setTaskId(null);
          setGenerationStartTime(null);
          setTaskStatus(null);
          fetchUserCredits();
          return true;
        }

        setProgress((prev) => Math.min(prev + 5, 95));
        return false;
      } catch (error: any) {
        console.error('Error polling task status:', error);
        toast.error(`Query task failed: ${error.message}`);
        setIsGenerating(false);
        setProgress(0);
        setTaskId(null);
        setGenerationStartTime(null);
        setTaskStatus(null);
        fetchUserCredits();
        return true;
      }
    };

    pollTask();

    const interval = setInterval(async () => {
      if (cancelled || !taskId) {
        clearInterval(interval);
        return;
      }
      const completed = await pollTask();
      if (completed) {
        clearInterval(interval);
      }
    }, POLL_INTERVAL);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [taskId, isGenerating, generationStartTime]);

  const handleDownloadImage = async (image: GeneratedImage) => {
    if (!image.url) {
      return;
    }

    try {
      setDownloadingImageId(image.id);
      const response = await fetch(
        `/api/proxy/file?url=${encodeURIComponent(image.url)}`
      );
      if (!response.ok) {
        throw new Error('Failed to fetch image');
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `zestygen-restored-${image.id}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 200);
      toast.success(t('success.downloaded'));
    } catch (error) {
      console.error('Download error:', error);
      toast.error(t('errors.download-failed'));
    } finally {
      setDownloadingImageId(null);
    }
  };

  return (
    <section className="py-4 md:py-6">
      <div className="container">
        <div className="mx-auto max-w-7xl">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {/* Left Column - Upload */}
            <Card>
              <CardHeader className="pb-4">
                {srOnlyTitle && <h2 className="sr-only">{srOnlyTitle}</h2>}
                <CardTitle className="flex items-center gap-2 text-xl font-semibold">
                  {t('upload.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pb-4">
                {/* Image Upload */}
                <div className="space-y-2">
                  <ImageUploader
                    defaultPreviews={referenceImageUrls}
                    onChange={(items) => {
                      setReferenceImageItems(items);
                      const urls = items
                        .filter((item) => item.status === 'uploaded' && item.url)
                        .map((item) => item.url!);
                      setReferenceImageUrls(urls);

                      const uploadedItems = items.filter((item) => item.status === 'uploaded');
                      if (uploadedItems.length > 0) {
                        const totalSizeMB = uploadedItems.reduce((sum, item) => {
                          return sum + (item.size || 0);
                        }, 0) / (1024 * 1024);

                        trackImageUpload({
                          image_count: uploadedItems.length,
                          total_size_mb: parseFloat(totalSizeMB.toFixed(2)),
                        });
                      }
                    }}
                    allowMultiple={false}
                    maxImages={1}
                    maxSizeMB={maxSizeMB}
                    emptyHint={t('upload.hint')}
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('upload.format')}
                  </p>
                </div>

                {/* Info Box */}
                <div className="rounded-lg border bg-muted/30 p-4 space-y-2">
                  <h4 className="text-sm font-semibold text-foreground">
                    {t('info.title')}
                  </h4>
                  <p className="text-xs text-foreground/80 leading-relaxed">
                    {t('info.description')}
                  </p>
                </div>

                {/* Credits Info */}
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <CreditCard className="h-3.5 w-3.5" />
                    <span>{t('credits.cost', { count: COST_CREDITS })}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <User className="h-3.5 w-3.5" />
                    <span>{t('credits.remaining', { count: remainingCredits })}</span>
                  </div>
                </div>

                {/* Generate Button */}
                <Button
                  onClick={handleGenerate}
                  disabled={
                    isGenerating ||
                    referenceImageUrls.length === 0
                  }
                  className="w-full"
                  size="lg"
                >
                  {isGenerating ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('buttons.restoring')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="mr-2 h-4 w-4" />
                      {t('buttons.restore')}
                    </>
                  )}
                </Button>

                {isGenerating && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">{Math.round(progress)}%</span>
                    </div>
                    <Progress value={progress} />
                    {taskStatus && (
                      <p className="text-center text-xs text-muted-foreground">
                        Status: {taskStatus}
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Right Column - Results */}
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2 text-xl font-semibold">
                  <ImageIcon className="h-5 w-5" />
                  {generatedImages.length > 0 ? t('results.title-restored') : t('results.title-preview')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {generatedImages.length === 0 ? (
                  <div className="space-y-4">
                    <div className="relative min-h-[500px] overflow-hidden rounded-lg border-2 border-dashed flex items-center justify-center">
                      <div className="text-center space-y-4 p-8">
                        <ImageIcon className="h-16 w-16 mx-auto text-muted-foreground/50" />
                        <div className="space-y-2">
                          <p className="text-sm font-medium text-foreground">
                            No restored photos yet
                          </p>
                          <p className="text-xs text-muted-foreground max-w-md">
                            Upload an old photo and click "Restore & Colorize Photo" to see the AI transformation
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <ScrollArea className="h-[600px] pr-4">
                      <div className="space-y-4">
                        {generatedImages.map((image) => (
                          <div
                            key={image.id}
                            className="group relative overflow-hidden rounded-lg border"
                          >
                            <LazyImage
                              src={image.url}
                              alt="Restored old photo"
                              className="w-full"
                            />
                            <div className="absolute right-2 top-2 flex gap-2 opacity-0 transition-opacity group-hover:opacity-100">
                              <Button
                                size="sm"
                                variant="secondary"
                                onClick={() => handleDownloadImage(image)}
                                disabled={downloadingImageId === image.id}
                              >
                                {downloadingImageId === image.id ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Download className="h-4 w-4" />
                                )}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </ScrollArea>
                    {/* Success Message */}
                    <div className="mt-6 rounded-lg border border-green-500/20 bg-gradient-to-r from-green-500/5 to-green-500/10 p-4">
                      <div className="text-center space-y-2">
                        <p className="text-sm font-medium text-foreground">
                          {t('results.success-title')}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {t('results.success-description')}
                        </p>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>
  );
}
